import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { Bell, User, Award, HelpCircle, LogOut, BookOpen, Settings, Shield, Mail, Smartphone, Gift, Star, Info, Footprints } from 'lucide-react';
import { useAuth, usePoints } from '../App';
import { motion } from 'framer-motion';
import { confirmDialog } from '../components/ui/confirm-dialog';
import notify from '../utils/notify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MenuPage = ({ TopHeader, BottomNav, setShowTour }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Check if user is HR Admin
  const isHRAdmin = user?.role === 'HR_ADMIN';

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread/count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const menuItems = [
    // {
    //   icon: <Star size={24} />,
    //   title: 'Restart Onboarding',
    //   description: 'Experience the welcome flow again',
    //   action: async () => {
    //     if (await confirmDialog({ title: 'Restart onboarding?', message: 'This will replay the welcome flow.', confirmText: 'Restart' })) {
    //       localStorage.removeItem('socialstar_onboarding');
    //       localStorage.removeItem('tour_completed');
    //       localStorage.removeItem('tour_progress');
    //       window.location.href = window.location.origin;
    //     }
    //   },
    //   testId: 'menu-onboarding',
    //   highlight: true
    // },
    // {
    //   icon: <Smartphone size={24} />,
    //   title: 'Connected Devices',
    //   description: 'Manage your fitness trackers',
    //   action: () => navigate('/devices'),
    //   testId: 'menu-devices'
    // },
    {
      icon: <Footprints size={24} />,
      title: 'Step Tracking',
      description: 'Track your daily steps',
      action: () => navigate('/steps'),
      testId: 'menu-steps'
    },
    // HR Admin menu item - only visible for HR_ADMIN role
    ...(isHRAdmin ? [{
      icon: <Shield size={24} />,
      title: 'HR Administration',
      description: 'Manage events & moderation',
      action: () => navigate('/hr-admin'),
      testId: 'menu-hr-admin',
      highlight: true,
      hrOnly: true
    }] : []),
    {
      icon: <Gift size={24} />,
      title: 'Rewards',
      description: 'Redeem your earned stars',
      action: () => navigate('/rewards'),
      testId: 'menu-rewards'
    },
    {
      icon: <Bell size={24} />,
      title: 'Notifications',
      description: 'View your activity updates',
      badge: unreadCount > 0 ? unreadCount : null,
      action: () => navigate('/notifications'),
      testId: 'menu-notifications'
    },
    {
      icon: <BookOpen size={24} />,
      title: 'Take Tour',
      description: 'Learn how to use SocialStar',
      action: () => {
        // Clear tour completion to allow replay
        localStorage.removeItem('tour_completed');
        localStorage.removeItem('tour_progress');
        setShowTour(true);
        navigate('/');
      },
      testId: 'menu-tour'
    },
    // {
    //   icon: <HelpCircle size={24} />,
    //   title: 'Help & Support',
    //   description: 'Get help with the platform',
    //   action: () => alert('Help & Support coming soon!'),
    //   testId: 'menu-help'
    // },
    {
      icon: <Mail size={24} />,
      title: 'Contact Us',
      description: 'Reach out to our team',
      action: () => window.open('mailto:support@socialstar.co'),
      testId: 'menu-contact'
    }
  ];

  const handleLogout = async () => {
    if (await confirmDialog({ title: 'Logout?', message: 'You will be returned to the sign-in screen.', confirmText: 'Logout' })) {
      logout();
      navigate('/login');
    }
  };

  return (
    <div className="page-container">
      <TopHeader title="Menu" showBack={false} />
      <div className="content-wrapper">
        <div className="content-section">
          <div style={{ textAlign: 'center', marginBottom: 32, padding: 24, background: 'linear-gradient(135deg, var(--primary-teal), var(--accent-teal))', borderRadius: 20, color: 'white' }}>
            <div
              style={{
                width: 80,
                height: 80,
                margin: '0 auto 16px',
                borderRadius: '50%',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32,
                fontWeight: 700,
                color: 'var(--primary-teal)'
              }}
            >
              {user.username.charAt(0).toUpperCase()}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{user.username}</h2>
            <p style={{ fontSize: 14, opacity: 0.9 }}>{user.email}</p>
            <p style={{ fontSize: 14, opacity: 0.9, marginTop: 8 }}>
              {user.role} • {user.company}
            </p>
          </div>

          <div>
            {menuItems.map((item, index) => (
              <div
                key={index}
                className="card"
                onClick={item.action}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 16, 
                  cursor: 'pointer', 
                  marginBottom: 12,
                  ...(item.highlight && {
                    background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
                    border: '2px solid #F5A623',
                    boxShadow: '0 4px 12px rgba(245, 166, 35, 0.2)'
                  })
                }}
                data-testid={item.testId}
              >
                <div style={{ 
                  color: item.highlight ? '#F5A623' : 'var(--primary-teal)', 
                  flexShrink: 0 
                }}>
                  {item.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: 16, 
                    fontWeight: 600, 
                    color: item.highlight ? '#92400E' : 'var(--text-dark)', 
                    marginBottom: 2 
                  }}>
                    {item.title}
                    {item.highlight && (
                      <span style={{ 
                        marginLeft: 8, 
                        fontSize: 20 
                      }}>✨</span>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: 13, 
                    color: item.highlight ? '#B45309' : 'var(--text-medium)' 
                  }}>
                    {item.description}
                  </div>
                </div>
                {item.badge && (
                  <div style={{
                    background: '#DC2626',
                    color: 'white',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700
                  }}>
                    {item.badge}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            className="btn-primary"
            style={{ background: '#DC2626', marginTop: 32 }}
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={20} style={{ display: 'inline', marginRight: 8 }} />
            Logout
          </button>

          <div style={{ marginTop: 32, padding: 20, background: 'var(--background-light)', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>socialstar v1.0</div>
            <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>Powered by socialstar</div>
          </div>
        </div>
      </div>
      <BottomNav active="menu" />
    </div>
  );
};

export const NotificationsPage = ({ TopHeader, BottomNav }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=50`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page-container">
      <TopHeader title="Notifications" showBack={true} />
      <div className="content-wrapper">
        <div className="content-section">
          {notifications.length > 0 && (
            <button
              className="btn-secondary"
              onClick={markAllAsRead}
              style={{ marginBottom: 20 }}
              data-testid="mark-all-read-btn"
            >
              Mark All as Read
            </button>
          )}

          {notifications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔔</div>
              <h3 className="empty-title">No Notifications</h3>
              <p className="empty-description">
                You'll see notifications here when you complete challenges, earn achievements, or reach milestones!
              </p>
            </div>
          ) : (
            <div data-testid="notifications-list">
              {notifications.map((notification) => {
                // Map notification type → a single dot colour. Avoid any image,
                // emoji, or external icon reference — strictly a coloured dot.
                const dotColor = (() => {
                  const t = notification.type || '';
                  if (t.includes('achievement') || t.includes('reward')) return '#F59E0B'; // amber
                  if (t.includes('streak') || t.includes('habit')) return '#EF4444';       // red
                  if (t.includes('approved') || t.includes('success')) return '#10B981';   // green
                  if (t.includes('rejected') || t.includes('error') || t.includes('fail')) return '#DC2626'; // red-600
                  if (t.includes('event') || t.includes('reminder')) return '#3B82F6';     // blue
                  if (t.includes('hr') || t.includes('admin')) return '#8B5CF6';           // purple
                  return '#4DB6AC';                                                        // teal (default)
                })();
                return (
                  <div
                    key={notification.id}
                    className="card"
                    onClick={() => !notification.read && markAsRead(notification.id)}
                    style={{
                      cursor: notification.read ? 'default' : 'pointer',
                      background: notification.read ? 'white' : '#F0FDFA',
                      borderLeft: notification.read ? '1px solid var(--border-light)' : '4px solid var(--primary-teal)',
                      marginBottom: 12
                    }}
                    data-testid="notification-item"
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                      <div
                        data-testid="notification-type-dot"
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: dotColor,
                          flexShrink: 0,
                          marginTop: 6,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>
                          {notification.title}
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--text-medium)', marginBottom: 8 }}>
                          {notification.message}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                          {new Date(notification.created_at).toLocaleString()}
                        </div>
                      </div>
                      {!notification.read && (
                        <div style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: 'var(--primary-teal)',
                          flexShrink: 0
                        }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <BottomNav active="menu" />
    </div>
  );
};

export const ConnectedDevicesPage = ({ TopHeader, BottomNav }) => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todaySteps, setTodaySteps] = useState(null);

  useEffect(() => {
    fetchDevices();
    fetchTodaySteps();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API}/devices`);
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaySteps = async () => {
    try {
      const response = await axios.get(`${API}/devices/steps/today`);
      setTodaySteps(response.data);
    } catch (error) {
      console.error('Failed to fetch today steps:', error);
    }
  };

  const handleConnectDevice = async (deviceType, deviceName) => {
    try {
      await axios.post(`${API}/devices/connect`, {
        device_type: deviceType,
        device_name: deviceName
      });
      fetchDevices();
      alert(`${deviceName} connected successfully!`);
    } catch (error) {
      console.error('Failed to connect device:', error);
      alert('Failed to connect device. Please try again.');
    }
  };

  const handleDisconnect = async (deviceId) => {
    if (!(await confirmDialog({ title: 'Disconnect device?', message: 'You will need to reconnect it to resume syncing.', confirmText: 'Disconnect', danger: true }))) {
      return;
    }

    try {
      await axios.delete(`${API}/devices/${deviceId}`);
      fetchDevices();
      notify.success('Device disconnected successfully!');
    } catch (error) {
      console.error('Failed to disconnect device:', error);
      notify.error('Failed to disconnect device. Please try again.');
    }
  };

  const availableDevices = [
    {
      type: 'health_connect',
      name: 'Phone Pedometer (Android)',
      icon: '📱',
      description: 'Connect via Health Connect',
      color: '#4CAF50'
    },
    {
      type: 'apple_health',
      name: 'Phone Pedometer (iPhone)',
      icon: '📱',
      description: 'Connect via Apple Health',
      color: '#000000'
    },
    {
      type: 'fitbit',
      name: 'Fitbit',
      icon: '⌚',
      description: 'Sync with Fitbit app',
      color: '#00B0B9'
    },
    {
      type: 'garmin',
      name: 'Garmin',
      icon: '⌚',
      description: 'Sync with Garmin Connect',
      color: '#007CC3'
    },
    {
      type: 'apple_watch',
      name: 'Apple Watch',
      icon: '⌚',
      description: 'Sync via Apple Health',
      color: '#000000'
    }
  ];

  const getDeviceStatus = (deviceType) => {
    return devices.find(d => d.device_type === deviceType);
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page-container">
      <TopHeader title="Connected Devices" showBack={true} />
      <div className="content-wrapper">
        <div className="content-section">
          {/* Info Banner */}
          <div style={{
            padding: 20,
            background: 'linear-gradient(135deg, #E0F2FE, #DBEAFE)',
            borderRadius: 16,
            marginBottom: 24,
            border: '2px solid #3B82F6'
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📱⌚</div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 8 }}>
              Automatic Step Tracking
            </h3>
            <p style={{ fontSize: 14, color: 'var(--text-medium)', lineHeight: 1.6 }}>
              Connect your phone or fitness tracker to automatically sync your daily steps. 
              Available when using our mobile app (iOS/Android).
            </p>
          </div>

          {/* Today's Synced Steps */}
          {todaySteps && todaySteps.synced && (
            <div style={{
              padding: 24,
              background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)',
              borderRadius: 16,
              marginBottom: 24,
              textAlign: 'center',
              border: '2px solid #10B981'
            }}>
              <div style={{ fontSize: 14, color: 'var(--text-medium)', marginBottom: 8 }}>
                Today's Synced Steps
              </div>
              <div style={{ fontSize: 48, fontWeight: 800, color: '#10B981', fontFamily: 'Inter' }}>
                {todaySteps.total_steps.toLocaleString()}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-medium)', marginTop: 4 }}>
                Last synced: {formatLastSync(todaySteps.activities[0]?.synced_at)}
              </div>
            </div>
          )}

          {/* Connected Devices Section */}
          {devices.length > 0 && (
            <>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 16 }}>
                ✅ Connected Devices
              </h3>
              {devices.map((device) => (
                <div
                  key={device.id}
                  className="card"
                  style={{
                    marginBottom: 16,
                    border: '2px solid #10B981',
                    background: 'linear-gradient(to right, #F0FDF4, #FFFFFF)'
                  }}
                >
                  <div style={{ display: 'flex', gap: 16, alignItems: 'start' }}>
                    <div style={{ fontSize: 48, flexShrink: 0 }}>
                      {availableDevices.find(d => d.type === device.device_type)?.icon || '📱'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>
                          {device.device_name}
                        </div>
                        <div style={{
                          padding: '2px 8px',
                          background: '#10B981',
                          color: 'white',
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: 600
                        }}>
                          CONNECTED
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-medium)', marginBottom: 8 }}>
                        {availableDevices.find(d => d.type === device.device_type)?.description}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                        Last synced: {formatLastSync(device.last_sync)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                    <button
                      className="btn-secondary"
                      style={{ flex: 1, fontSize: 14 }}
                      onClick={() => fetchTodaySteps()}
                    >
                      Sync Now
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ flex: 1, fontSize: 14, color: '#DC2626', borderColor: '#DC2626' }}
                      onClick={() => handleDisconnect(device.id)}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Available Devices Section */}
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)', marginTop: 32, marginBottom: 16 }}>
            📱 Available Devices
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-medium)', marginBottom: 16 }}>
            Connect these devices to automatically track your steps:
          </p>

          {availableDevices.map((device) => {
            const connected = getDeviceStatus(device.type);
            
            return (
              <div
                key={device.type}
                className="card"
                style={{
                  marginBottom: 12,
                  opacity: connected ? 0.6 : 1,
                  border: connected ? '1px solid var(--border-light)' : '2px solid var(--border-light)'
                }}
              >
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ fontSize: 48, flexShrink: 0 }}>
                    {device.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>
                      {device.name}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>
                      {device.description}
                    </div>
                  </div>
                  {connected ? (
                    <div style={{
                      padding: '6px 12px',
                      background: '#10B981',
                      color: 'white',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      CONNECTED
                    </div>
                  ) : (
                    <button
                      className="btn-primary"
                      style={{ fontSize: 14, padding: '8px 16px' }}
                      onClick={() => handleConnectDevice(device.type, device.name)}
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Info Footer */}
          <div style={{
            marginTop: 32,
            padding: 20,
            background: 'var(--background-light)',
            borderRadius: 12,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🚀</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 8 }}>
              Coming in Mobile App
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.6 }}>
              These integrations will be fully functional in our upcoming iOS and Android apps. 
              For now, continue logging activities manually.
            </div>
          </div>

          {/* How It Works */}
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 16 }}>
              How It Works
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'var(--primary-teal)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0
                }}>
                  1
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>
                    Connect Your Device
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>
                    Tap "Connect" to authorize SocialStar to read your step data
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'var(--primary-teal)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0
                }}>
                  2
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>
                    Automatic Syncing
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>
                    Your steps sync automatically throughout the day
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'var(--primary-teal)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0
                }}>
                  3
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>
                    Track Progress
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>
                    View your progress in challenges without manual logging
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BottomNav active="menu" />
    </div>
  );
};

export const RewardsPage = ({ TopHeader, BottomNav, useAuth }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  // The TopHeader reads its star pill from PointsContext, and after
  // logging an action every relevant page (ChallengesPages, HabitsPage,
  // CreatePostPage, this page's own handleRedeem) already calls
  // refreshPoints() on it — that's the app's shared live-balance
  // channel. RewardsPage now reads its balance from the SAME context so
  // the "YOUR STARS BALANCE" tile updates the instant a new activity is
  // logged elsewhere, without a page reload. The previous read from
  // `user.points` sat on the auth snapshot, which only refreshed on
  // login mount + explicit refreshUser() calls — hence the stale value.
  const { points, refreshPoints } = usePoints();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  // Coupon modal shown after a successful redemption. Stays open until the
  // user clicks Close — we deliberately do NOT auto-dismiss or reload the page.
  const [couponModal, setCouponModal] = useState(null); // { code, rewardTitle, pointsRemaining }
  const [couponCopied, setCouponCopied] = useState(false);
  
  const userStars = points || 0;

  // Rewards are disabled for the demo-contest org. Backend also blocks
  // POST /rewards/{id}/redeem, so this is a UX gate only. Any other org
  // stays completely unaffected — the check is a single string compare.
  const isDemoOrg = user?.org_id === 'demo-contest-2026';

  // Professional color palette
  const COLORS = {
    orange: '#D97706',
    orangeLight: '#FEF3C7',
    orangeMuted: '#B45309',
    teal: '#0D9488',
    tealLight: '#E6FFFA',
    tealMuted: '#115E59',
    surface: '#FAFAFA',
    border: '#E5E7EB',
    textPrimary: '#1F2937',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
    success: '#059669'
  };

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await axios.get(`${API}/rewards`);
      setRewards(response.data);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (rewardId, rewardStars) => {
    if (userStars < rewardStars) {
      notify.error('Insufficient stars for this reward.');
      return;
    }

    if (!(await confirmDialog({
      title: 'Redeem reward?',
      message: `${rewardStars} stars will be deducted from your balance.`,
      confirmText: 'Redeem',
    }))) {
      return;
    }

    try {
      const { data } = await axios.post(`${API}/rewards/${rewardId}/redeem`);
      // Pop the coupon modal with the generated code from the backend.
      // Stays open until the user dismisses it — no auto-close, no reload.
      setCouponModal({
        code: data?.coupon_code || '',
        rewardTitle: data?.reward || '',
        pointsRemaining: data?.points_remaining,
      });
      setCouponCopied(false);
      // Refresh the rewards list AND the user's star balance so the top
      // header pill and the local userStars value update immediately —
      // without forcing a full page reload.
      fetchRewards();
      if (refreshUser) refreshUser();
      if (refreshPoints) refreshPoints();
    } catch (error) {
      console.error('Failed to redeem reward:', error);
      notify.error(error?.response?.data?.detail || 'Unable to redeem reward. Please try again.');
    }
  };

  const handleCopyCoupon = async () => {
    if (!couponModal?.code) return;
    try {
      await navigator.clipboard.writeText(couponModal.code);
      setCouponCopied(true);
      // The "Copied!" affordance reverts after a moment so a second copy
      // still gives visual feedback — the modal itself stays open.
      setTimeout(() => setCouponCopied(false), 2000);
    } catch {
      // Clipboard API can be blocked (e.g. http previews, sandboxed iframes).
      // Fall back to a manual selection so the user can copy by hand.
      const ta = document.createElement('textarea');
      ta.value = couponModal.code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCouponCopied(true); setTimeout(() => setCouponCopied(false), 2000); } catch { /* ignore — user can still copy manually */ }
      document.body.removeChild(ta);
    }
  };

  // Category labels
  const getCategoryLabel = (category) => {
    const labels = {
      'perks': 'PERKS',
      'privileges': 'PRIVILEGES',
      'donations': 'DONATIONS',
      'experiences': 'EXPERIENCES'
    };
    return labels[category] || 'REWARD';
  };

  // Inline icon component - minimal flat style
  const RewardIcon = ({ category }) => {
    const iconPaths = {
      'perks': (
        // Coffee cup - minimal
        <path d="M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8zM6 1v3M10 1v3M14 1v3" 
          stroke={COLORS.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      ),
      'privileges': (
        // Calendar/flexibility
        <path d="M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM16 2v4M8 2v4M3 10h18M9 16h6" 
          stroke={COLORS.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      ),
      'donations': (
        // Tree/nature
        <path d="M12 22v-7M9 9l3-5 3 5H9zM7 14l5-8 5 8H7z" 
          stroke={COLORS.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      ),
      'experiences': (
        // Certificate/learning
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M9 13h6 M9 17h6" 
          stroke={COLORS.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      )
    };

    return (
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 8,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          {iconPaths[category] || iconPaths['experiences']}
        </svg>
      </div>
    );
  };

  // Star icon component
  const StarIcon = ({ size = 14, color = COLORS.orange }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
        fill={color}/>
    </svg>
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (isDemoOrg) {
    return (
      <div className="page-container">
        <TopHeader title="Rewards" showBack={true} />
        <div className="content-wrapper">
          <div className="content-section" style={{ padding: '0 16px' }}>
            <div
              data-testid="rewards-demo-disabled"
              style={{
                marginTop: 40, padding: '32px 24px', background: '#FFFFFF',
                borderRadius: 16, border: '1px solid #E5E7EB', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 6 }}>
                Rewards are disabled for demo accounts
              </div>
              <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                You can still explore actions and track your impact. Rewards will be enabled for real teams that upgrade from the demo.
              </div>
            </div>
          </div>
        </div>
        <BottomNav active="menu" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <TopHeader title="Rewards" showBack={true} />
      <div className="content-wrapper">
        <div className="content-section" style={{ padding: '0 16px' }}>
          
          {/* Stars Balance Header with Animated Star */}
          <div style={{
            background: '#FFFFFF',
            borderRadius: 16,
            padding: '28px 24px',
            marginBottom: 20,
            border: `1px solid ${COLORS.border}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Left side - Balance info */}
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8
              }}>
                <div style={{ 
                  fontSize: 12, 
                  fontWeight: 600, 
                  color: COLORS.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px'
                }}>
                  Your Stars Balance
                </div>
                {/* Info Icon with Tooltip */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Info size={16} color={COLORS.textMuted} />
                  </button>
                  {/* Tooltip */}
                  {showInfoTooltip && (
                    <div 
                      onClick={() => setShowInfoTooltip(false)}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginTop: 8,
                        background: '#2C3E50',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: 8,
                        fontSize: 12,
                        lineHeight: 1.5,
                        width: 220,
                        zIndex: 100,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      Complete Actions, share updates, and maintain streaks to earn more stars.
                      <div style={{
                        position: 'absolute',
                        top: -6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderBottom: '6px solid #2C3E50'
                      }} />
                    </div>
                  )}
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'baseline', 
                gap: 4,
                marginBottom: 6
              }}>
                <span style={{ 
                  fontSize: 44, 
                  fontWeight: 700, 
                  color: COLORS.orange,
                  lineHeight: 1,
                  letterSpacing: '-1px'
                }}>
                  {userStars.toLocaleString()}
                </span>
              </div>
              <div style={{ 
                fontSize: 14, 
                color: COLORS.textSecondary 
              }}>
                Available to redeem
              </div>
            </div>

            {/* Right side - Animated Star */}
            <div style={{ 
              position: 'relative',
              width: 100,
              height: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* Glow effect */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(251, 146, 60, 0.3) 0%, rgba(251, 146, 60, 0) 70%)'
                }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              />
              {/* Main animated star */}
              <motion.div
                animate={{
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              >
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FCD34D" />
                      <stop offset="50%" stopColor="#FB923C" />
                      <stop offset="100%" stopColor="#F97316" />
                    </linearGradient>
                    <filter id="starShadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#FB923C" floodOpacity="0.4"/>
                    </filter>
                  </defs>
                  <path 
                    d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                    fill="url(#starGradient)"
                    filter="url(#starShadow)"
                  />
                </svg>
              </motion.div>
              {/* Sparkle particles */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: '#FCD34D'
                  }}
                  animate={{
                    x: [0, (i - 1) * 30],
                    y: [0, (i % 2 === 0 ? -1 : 1) * 20],
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.5,
                    ease: 'easeOut'
                  }}
                />
              ))}
            </div>
          </div>

          {/* Section Title */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}>
            <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: COLORS.teal,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}>
              Available Rewards
            </div>
            <button
              type="button"
              data-testid="view-redemption-history-btn"
              onClick={() => navigate('/rewards/history')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.teal,
                textDecoration: 'underline',
              }}
            >
              View Redemption History
            </button>
          </div>

          {/* Rewards List */}
          {rewards.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              background: COLORS.surface,
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`
            }}>
              <div style={{ fontSize: 14, color: COLORS.textMuted }}>
                No rewards available at this time.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rewards.map((reward) => {
                const rewardCost = reward.cost_points || reward.points_cost || 0;
                const isEligible = userStars >= rewardCost;
                const starsNeeded = rewardCost - userStars;
                
                return (
                  <div
                    key={reward.id}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 12,
                      padding: '18px',
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    {/* Main Content Row */}
                    <div style={{ display: 'flex', gap: 14 }}>
                      
                      {/* Left: Inline Icon */}
                      <RewardIcon category={reward.category} />
                      
                      {/* Center: Title, Category, Description */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ 
                          fontSize: 15, 
                          fontWeight: 600, 
                          color: COLORS.textPrimary, 
                          marginBottom: 3,
                          lineHeight: 1.3
                        }}>
                          {reward.title}
                        </h3>
                        <div style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: COLORS.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: 8
                        }}>
                          {getCategoryLabel(reward.category)}
                        </div>
                        <p style={{ 
                          fontSize: 13, 
                          color: COLORS.textSecondary, 
                          lineHeight: 1.45,
                          margin: 0
                        }}>
                          {reward.description}
                        </p>
                      </div>
                      
                      {/* Right: Star Cost */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                        flexShrink: 0
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}>
                          <StarIcon size={14} color={COLORS.orange} />
                          <span style={{ 
                            fontSize: 16, 
                            fontWeight: 700, 
                            color: COLORS.orange
                          }}>
                            {rewardCost}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom Row: Eligibility + Redeem Button */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginTop: 14,
                      paddingTop: 14,
                      borderTop: `1px solid ${COLORS.border}`
                    }}>
                      {/* Eligibility Status - FIXED LOGIC */}
                      <div style={{ 
                        fontSize: 13,
                        color: isEligible ? COLORS.success : COLORS.textMuted,
                        fontWeight: 500
                      }}>
                        {isEligible ? (
                          'Eligible for redemption'
                        ) : (
                          `Need ${starsNeeded.toLocaleString()} more stars`
                        )}
                      </div>
                      
                      {/* Redeem Button - Only shown if eligible */}
                      {isEligible ? (
                        <button
                          disabled={!reward.available}
                          onClick={() => handleRedeem(reward.id, rewardCost)}
                          style={{
                            padding: '10px 20px',
                            fontSize: 13,
                            fontWeight: 600,
                            borderRadius: 8,
                            border: 'none',
                            cursor: 'pointer',
                            background: COLORS.teal,
                            color: '#FFFFFF',
                            transition: 'opacity 0.15s ease'
                          }}
                        >
                          Redeem
                        </button>
                      ) : (
                        <div style={{
                          padding: '10px 16px',
                          fontSize: 13,
                          fontWeight: 500,
                          borderRadius: 8,
                          background: COLORS.surface,
                          color: COLORS.textMuted,
                          border: `1px solid ${COLORS.border}`
                        }}>
                          Locked
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* How to Earn Section */}
          <div style={{ 
            marginTop: 28, 
            padding: '18px', 
            background: COLORS.surface, 
            borderRadius: 12,
            border: `1px solid ${COLORS.border}`
          }}>
            <div style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: COLORS.teal,
              marginBottom: 14,
              textTransform: 'uppercase',
              letterSpacing: '0.8px'
            }}>
              How to Earn Stars
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { title: 'Log activities', desc: 'Earn stars for steps, meditation, and workouts' },
                { title: 'Share updates', desc: 'Post your progress and inspire others' },
                { title: 'Maintain streaks', desc: 'Consistent activity earns bonus rewards' },
                { title: 'Join team actions', desc: 'Collaborate for multiplied earnings' }
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: COLORS.teal,
                    marginTop: 7,
                    flexShrink: 0
                  }} />
                  <div style={{ fontSize: 13, color: COLORS.textPrimary, lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 600 }}>{item.title}</span>
                    <span style={{ color: COLORS.textSecondary }}> — {item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ height: 24 }} />
        </div>
      </div>

      {/* Coupon modal — shown after a successful redemption. */}
      {couponModal && (
        <div
          data-testid="coupon-modal-backdrop"
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, backdropFilter: 'blur(4px)',
          }}
        >
          <div
            data-testid="coupon-modal"
            style={{
              width: '100%', maxWidth: 420, background: '#FFFFFF',
              borderRadius: 18, padding: '28px 24px 24px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              textAlign: 'center',
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: COLORS.tealLight, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <Gift size={26} color={COLORS.teal} />
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '1px',
              color: COLORS.success, marginBottom: 6, textTransform: 'uppercase',
            }}>
              Redemption successful
            </div>
            <h2 style={{
              fontSize: 18, fontWeight: 700, color: COLORS.textPrimary,
              margin: '0 0 4px',
            }}>
              {couponModal.rewardTitle || 'Reward'}
            </h2>
            <p style={{
              fontSize: 13, color: COLORS.textSecondary, margin: '0 0 18px',
            }}>
              Use the code below at checkout to claim your reward.
            </p>

            <div style={{
              border: `1.5px dashed ${COLORS.teal}`,
              background: COLORS.tealLight,
              borderRadius: 12,
              padding: '18px 14px',
              marginBottom: 16,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: COLORS.tealMuted,
                letterSpacing: '0.8px', marginBottom: 6, textTransform: 'uppercase',
              }}>Coupon code</div>
              <div
                data-testid="coupon-modal-code"
                style={{
                  fontSize: 26, fontWeight: 800, color: COLORS.textPrimary,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  letterSpacing: '2px', wordBreak: 'break-all', lineHeight: 1.2,
                }}
              >
                {couponModal.code || '—'}
              </div>
            </div>

            {typeof couponModal.pointsRemaining === 'number' && (
              <div style={{
                fontSize: 12, color: COLORS.textSecondary, marginBottom: 18,
              }}>
                Stars remaining: <strong style={{ color: COLORS.textPrimary }}>{couponModal.pointsRemaining}</strong>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                data-testid="coupon-modal-copy-btn"
                onClick={handleCopyCoupon}
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 10,
                  background: couponCopied ? COLORS.success : COLORS.teal,
                  color: '#FFFFFF', border: 'none', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', transition: 'background 0.2s ease',
                }}
              >
                {couponCopied ? 'Copied!' : 'Copy Code'}
              </button>
              <button
                type="button"
                data-testid="coupon-modal-close-btn"
                onClick={() => setCouponModal(null)}
                style={{
                  flex: 1, padding: '11px 14px', borderRadius: 10,
                  background: '#FFFFFF', color: COLORS.textPrimary,
                  border: `1px solid ${COLORS.border}`, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="menu" />
    </div>
  );
};

export const RedemptionHistoryPage = ({ TopHeader, BottomNav }) => {
  // Simple list view of the current user's past reward redemptions.
  // Data source: GET /api/redemptions/my — returns docs from the
  // `redemptions` collection scoped to current_user.id, sorted by
  // redeemed_at desc. Card styling mirrors the Available Rewards list
  // on RewardsPage so the two feel visually consistent.
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const COLORS = {
    orange: '#D97706',
    teal: '#0D9488',
    tealLight: '#E6FFFA',
    surface: '#FAFAFA',
    border: '#E5E7EB',
    textPrimary: '#1F2937',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/redemptions/my`);
        setRedemptions(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Failed to fetch redemption history:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page-container">
      <TopHeader title="Redemption History" showBack={true} />
      <div className="content-wrapper">
        <div className="content-section" style={{ padding: '0 16px' }}>
          {redemptions.length === 0 ? (
            <div
              data-testid="redemption-history-empty"
              style={{
                textAlign: 'center',
                padding: '48px 24px',
                background: COLORS.surface,
                borderRadius: 12,
                border: `1px solid ${COLORS.border}`,
                marginTop: 12,
              }}
            >
              <div style={{ fontSize: 14, color: COLORS.textMuted }}>
                You haven't redeemed any rewards yet.
              </div>
            </div>
          ) : (
            <div
              data-testid="redemption-history-list"
              style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}
            >
              {redemptions.map((r) => (
                <div
                  key={r.id || r.coupon_code || r.redeemed_at}
                  data-testid="redemption-history-item"
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 12,
                    padding: '16px 18px',
                    border: `1px solid ${COLORS.border}`,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginBottom: 10,
                  }}>
                    <h3 style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                      margin: 0,
                      lineHeight: 1.3,
                    }}>
                      {r.reward_title || 'Reward'}
                    </h3>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      flexShrink: 0,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                          fill={COLORS.orange}
                        />
                      </svg>
                      <span style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: COLORS.orange,
                      }}>
                        {(r.points_spent ?? 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    border: `1px dashed ${COLORS.teal}`,
                    background: COLORS.tealLight,
                    borderRadius: 8,
                    padding: '8px 12px',
                    marginBottom: 8,
                  }}>
                    <div style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: COLORS.textMuted,
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                      marginBottom: 2,
                    }}>
                      Coupon code
                    </div>
                    <div
                      data-testid="redemption-history-code"
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: COLORS.textPrimary,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        letterSpacing: '1.5px',
                        wordBreak: 'break-all',
                      }}
                    >
                      {r.coupon_code || '—'}
                    </div>
                  </div>

                  <div style={{
                    fontSize: 12,
                    color: COLORS.textSecondary,
                  }}>
                    Redeemed on {formatDate(r.redeemed_at)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ height: 24 }} />
        </div>
      </div>
      <BottomNav active="menu" />
    </div>
  );
};
